/**
 * AgentThreadsPanel — the middle of the Agents tab when an agent (not Kiaan main) is picked (SUN-70).
 * Uses the chat-thread backend (SUN-61 / SUN-66): several chats per agent, each with its own memory.
 *
 *   Left strip: the agent's chats (newest first, unread count) · "+ New chat" · "Show archived"
 *   Right:      the open chat — header (rename / archive), messages oldest first, the send box
 *
 * A new chat starts with its first message (the server needs one). Rename and archive never
 * delete anything. A paused agent can be read, not messaged; an archived chat cannot be sent to.
 * Files and pictures (SUN-82): 📎 button, drop, or paste in an open chat; a new chat takes text first.
 * The open chat is polled every 15 s for the agent's reply. The server refuses anyone without
 * kiaan_manage_agents; the tab only mounts this for them.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  createAgentThread, getThreadMessages, kiaanErrorText, kiaanServerText, listAgentThreads, sendThreadMessage, updateAgentThread,
  uploadThreadFiles,
  type AgentThread, type TalkMessage, type TalkStatus, type TalkYou,
} from './kiaanApi';
import { AgentMessageBody } from './AgentMessageBody';
import { agoText, clockText } from './livework/timeWords';
import { AttachButton, MessageFiles, PendingFiles, useChatUploads } from './ChatAttachments';

export const PAUSED_NOTE = 'Paused — messages wait until it is turned on';
export const ARCHIVED_NOTE = 'This chat is archived — un-archive it to send';
const MAX = 2000;
const PILL: Record<string, string> = { working: 'kw-wip', ready: 'kw-ok', off: 'kw-no', paused: 'kw-wt' };
const NEW = '__new__';

export interface AgentThreadsPanelProps {
  agentId: string;
  agentName: string;
  /** SUN-97: replaces "Talking to {agentName}" (Kiaan · Manager chat). */
  headline?: string;
  /** SUN-110c: Kiaan · Manager header facts — who is asking and what the answers come from. */
  you?: TalkYou;
  /** Shown only when a number (Kiaan · Manager sends null: no credits line). */
  creditsPerMessage?: number | null;
  /** Fold the widget (a lesson walk runs on the page behind it). */
  onCollapse?: () => void;
  /** Reply poll; tests pass 0. */
  refreshMs?: number;
  now?: () => Date;
}

export function AgentThreadsPanel({ agentId, agentName, headline, you, creditsPerMessage, onCollapse, refreshMs = 15000, now = () => new Date() }: AgentThreadsPanelProps) {
  const [threads, setThreads] = useState<AgentThread[] | null>(null);
  const [status, setStatus] = useState<TalkStatus | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [messages, setMessages] = useState<TalkMessage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [newName, setNewName] = useState('');
  const [sending, setSending] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);

  const loadThreads = useCallback(async () => {
    try {
      const list = await listAgentThreads(agentId, showArchived);
      setThreads(list.threads);
      if (list.agent) setStatus(list.agent.status);
      setError(null);
      // First load: open the newest chat; none yet → the "new chat" box.
      setOpenId((cur) => cur ?? (list.threads.find((t) => !t.archived)?.id ?? NEW));
    } catch (err) {
      setThreads((t) => t ?? []);
      setError(kiaanErrorText(err));
    }
  }, [agentId, showArchived]);

  useEffect(() => {
    setThreads(null);
    setOpenId(null);
    setMessages(null);
    setText('');
    setStatus(null);
  }, [agentId]);

  useEffect(() => { void loadThreads(); }, [loadThreads]);

  const loadMessages = useCallback(async (id: string) => {
    try {
      const m = await getThreadMessages(id);
      setMessages(m.messages);
      if (m.agent) setStatus(m.agent.status);
      setError(null);
    } catch (err) {
      setError(kiaanErrorText(err));
    }
  }, []);

  useEffect(() => {
    if (!openId || openId === NEW) { setMessages(null); return; }
    setMessages(null);
    void loadMessages(openId);
    if (!refreshMs) return;
    const t = setInterval(() => void loadMessages(openId), refreshMs);
    return () => clearInterval(t);
  }, [openId, loadMessages, refreshMs]);

  const open = threads?.find((t) => t.id === openId) ?? null;
  const paused = status === 'paused';
  const archived = !!open?.archived;
  const trimmed = text.trim();
  const isNew = openId === NEW;

  const uploadHere = useCallback(
    (files: File[]) => uploadThreadFiles(openId as string, files),
    [openId],
  );
  const uploads = useChatUploads(openId && openId !== NEW && !paused && !archived ? uploadHere : null);
  const { clear: clearUploads } = uploads;
  // Files belong to one chat: switching chat drops the waiting ones.
  useEffect(() => { clearUploads(); }, [openId]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasFiles = uploads.pending.length > 0;

  async function send() {
    if ((!trimmed && !hasFiles) || trimmed.length > MAX || sending || uploads.busy || paused || archived) return;
    setSending(true);
    try {
      if (isNew) {
        const { thread, message } = await createAgentThread(agentId, trimmed, newName.trim() || undefined);
        setThreads((list) => [{ ...thread, lastMessageAt: message?.at ?? thread.lastMessageAt }, ...(list ?? [])]);
        setOpenId(thread.id);
        setNewName('');
      } else if (openId) {
        const message = await sendThreadMessage(openId, trimmed, uploads.pending.map((a) => a.id));
        setMessages((m) => [...(m ?? []), message]);
        setThreads((list) => (list ?? []).map((t) => (t.id === openId ? { ...t, lastMessageAt: message.at ?? t.lastMessageAt } : t)));
      }
      setText('');
      clearUploads();
      setError(null);
    } catch (err) {
      setError(kiaanServerText(err));
    } finally {
      setSending(false);
    }
  }

  async function patch(id: string, body: { name?: string; archived?: boolean }) {
    try {
      const fresh = await updateAgentThread(id, body);
      setThreads((list) => {
        const next = (list ?? []).map((t) => (t.id === id ? { ...t, ...fresh } : t));
        // Archived chats leave the list unless "Show archived" is on.
        return showArchived ? next : next.filter((t) => !t.archived);
      });
      if (body.archived && !showArchived && openId === id) setOpenId(NEW);
      setError(null);
    } catch (err) {
      setError(kiaanServerText(err));
    }
  }

  return (
    <div className="kw-talk kw-threads" data-testid="agent-threads">
      <div className="kw-talk-who">
        <span className="kw-av kw-ai">{agentName.charAt(0).toUpperCase()}</span>
        <div style={{ minWidth: 0 }}>
          <b>{headline ?? `Talking to ${agentName}`}</b>
          {status && <span className={`kw-pill kw-ml ${PILL[status] ?? ''}`} data-testid="talk-status">{status}</span>}
          {you ? (
            <div className="kw-small" data-testid="talk-you">
              You: <b>{you.name} · {you.roleLabel}</b> — answers from {you.scopeText}
              {typeof creditsPerMessage === 'number' ? ` · ${creditsPerMessage} credits per message` : ''}
            </div>
          ) : (
            <div className="kw-small">Each chat has its own memory. Work requests become their own task.</div>
          )}
        </div>
      </div>

      <div className="kw-th-cols">
        <nav className="kw-th-list" aria-label="Chats">
          <button type="button" className={`kw-it${isNew ? ' kw-on' : ''}`} onClick={() => setOpenId(NEW)} disabled={paused}>
            ＋ New chat
          </button>
          {threads === null && <div className="kw-small">Loading…</div>}
          {threads?.length === 0 && <div className="kw-small" data-testid="no-threads">No chats yet.</div>}
          {threads?.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`kw-it${openId === t.id ? ' kw-on' : ''}${t.archived ? ' kw-th-arch' : ''}`}
              onClick={() => setOpenId(t.id)}
              data-testid="thread-item"
              title={t.lastMessageAt ? `Last message ${agoText(t.lastMessageAt, now())}` : undefined}
            >
              <span className="kw-th-name">{t.name}</span>
              {t.unread > 0 && <span className="kw-n" aria-label={`${t.unread} unread`}>{t.unread}</span>}
            </button>
          ))}
          <label className="kw-small kw-th-arch-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
          </label>
        </nav>

        <div className="kw-talk" {...uploads.dropProps}>
          {open && (
            <div className="kw-th-head">
              {renaming !== null ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const n = renaming.trim();
                    if (n && n !== open.name) void patch(open.id, { name: n });
                    setRenaming(null);
                  }}
                  style={{ display: 'flex', gap: 6, flex: 1 }}
                >
                  <input className="kw-input" value={renaming} onChange={(e) => setRenaming(e.target.value)} maxLength={120} aria-label="Chat name" autoFocus />
                  <button type="submit" className="kw-btn">Save</button>
                  <button type="button" className="kw-btn kw-light" onClick={() => setRenaming(null)}>Cancel</button>
                </form>
              ) : (
                <>
                  <b className="kw-th-name" data-testid="thread-title">{open.name}</b>
                  {open.identifier && <span className="kw-small">{open.identifier}</span>}
                  <span className="kw-sp" />
                  <button type="button" className="kw-btn kw-light" onClick={() => setRenaming(open.name)}>Rename</button>
                  <button type="button" className="kw-btn kw-light" onClick={() => void patch(open.id, { archived: !open.archived })}>
                    {open.archived ? 'Un-archive' : 'Archive'}
                  </button>
                </>
              )}
            </div>
          )}

          <div className="kw-thread" aria-live="polite">
            {isNew && (
              <>
                <div className="kw-sys">A new chat with {agentName}. Your first message starts it.</div>
                <input
                  className="kw-input"
                  placeholder="Chat name (optional — the first line is used)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={120}
                  aria-label="New chat name"
                />
              </>
            )}
            {!isNew && messages && messages.length === 0 && <div className="kw-sys">No messages yet.</div>}
            {!isNew && messages?.map((m) => (
              <div key={m.id} className={`kw-msg kw-ink ${m.from === 'user' ? 'kw-me' : 'kw-ag'}`} data-testid="talk-msg">
                <div className="kw-small">
                  <b>{m.from === 'user' ? m.name : agentName}</b>
                  {m.at ? ` · ${clockText(m.at)} · ${agoText(m.at, now())}` : ''}
                </div>
                {m.text && (m.from === 'agent'
                  ? <AgentMessageBody text={m.text} onCollapse={onCollapse} />
                  : <div className="kw-msg-text">{m.text}</div>)}
                <MessageFiles files={m.attachments} />
              </div>
            ))}
          </div>

          {error && <div className="kw-banner" role="status">{error}</div>}
          {paused && <div className="kw-banner" role="status">{PAUSED_NOTE}</div>}
          {archived && <div className="kw-banner" role="status">{ARCHIVED_NOTE}</div>}
          <PendingFiles uploads={uploads} />
          <div className="kw-ask">
            <AttachButton
              uploads={uploads}
              disabled={paused || archived || isNew || !openId}
              title={isNew ? 'Send your first message, then attach files' : undefined}
            />
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={uploads.onPaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder={paused ? PAUSED_NOTE : archived ? ARCHIVED_NOTE : `Message ${agentName}…`}
              maxLength={MAX}
              rows={2}
              disabled={paused || archived || !openId}
              aria-label={`Message ${agentName}`}
            />
            <button type="button" className="kw-btn" onClick={() => void send()} disabled={paused || archived || sending || uploads.busy || (!trimmed && !hasFiles)}>
              {sending ? 'Sending…' : isNew ? 'Start chat' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
